import { makeAutoObservable, runInAction } from 'mobx'
import {
  getAuth,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  onAuthStateChanged,
  signOut,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
} from 'firebase/auth'
import { getDocument, setDocument } from '../api/firestore'

class AuthStore {
  currentUser = null
  isAuthChecked = false

  isLoading = false

  constructor(rootStore) {
    // It's crucial to mark `rootStore` as `false` in makeAutoObservable
    // to prevent MobX from trying to make the entire root store observable,
    // which would lead to an infinite recursion.
    makeAutoObservable(this, { rootStore: false }, { autoBind: true })
    this.rootStore = rootStore
    this.errorStore = rootStore.errorStore // Для консистентного отображения ошибок
    this.auth = getAuth()

    // Слушатель изменения состояния аутентификации
    onAuthStateChanged(this.auth, (user) => {
      runInAction(() => {
        this.currentUser = user
        this.isAuthChecked = true
      })
    })
  }

  // --- Actions для форм ---

  async login(data, setError) {
    this.isLoading = true
    try {
      const { email, password } = data
      await signInWithEmailAndPassword(this.auth, email, password)
      // onAuthStateChanged обработает редирект
    } catch (error) {
      // Вместо общей ошибки устанавливаем ошибку для конкретных полей.
      // Firebase из соображений безопасности не уточняет, что именно неверно - email или пароль.
      if (
        [
          'auth/user-not-found',
          'auth/wrong-password',
          'auth/invalid-credential',
        ].includes(error.code)
      ) {
        setError('email', {
          type: 'manual',
          message: 'Неверный email или пароль.',
        })
        setError('password', { type: 'manual', message: '' }) // Можно не дублировать сообщение
      } else {
        this.errorStore.showError('Произошла непредвиденная ошибка.')
      }
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  async resetPassword(email) {
    if (!email) {
      this.errorStore.showError('Сначала введите email в поле выше.')
      return
    }
    try {
      await sendPasswordResetEmail(this.auth, email)
      this.errorStore.showSuccess('Ссылка для сброса пароля отправлена.')
    } catch (error) {
      this.errorStore.showError('Ошибка при сбросе пароля')
    }
  }

  async register(data, setError) {
    this.isLoading = true
    try {
      const { email, password } = data
      const userCredential = await createUserWithEmailAndPassword(
        this.auth,
        email,
        password,
      )
      const user = userCredential.user
      console.log('user:', user)

      // Создаем документ пользователя в Firestore
      // со структурой, согласованной с OrganizationStore
      await setDocument('users', user.uid, {
        name: user.displayName || '', // Имя пока пустое, пользователь заполнит в профиле
        email: user.email,
        phone: user.phoneNumber || '', // Добавляем телефон, если он есть, иначе пустую строку
        roles: [],
        organizationIds: [],
      })

      // onAuthStateChanged обработает успешный вход и редирект
    } catch (error) {
      console.error('Ошибка при регистрации:', error)
      if (error.code === 'auth/email-already-in-use') {
        setError('email', {
          type: 'manual',
          message: 'Этот email уже зарегистрирован.',
        })
        this.errorStore.showError('Этот email уже зарегистрирован.')
      } else {
        this.errorStore.showError('Ошибка при регистрации.')
      }
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  async signInWithGoogle() {
    this.isLoading = true
    const provider = new GoogleAuthProvider()
    try {
      const result = await signInWithPopup(this.auth, provider)
      console.log('result:', result)
      const user = result.user

      // Проверяем, существует ли уже документ для этого пользователя
      const userDoc = await getDocument('users', user.uid)

      if (!userDoc) {
        // Если пользователь новый, создаем для него документ в Firestore
        console.log(
          `Новый пользователь через Google: ${user.uid}. Создаем документ...`,
        )
        await setDocument('users', user.uid, {
          name: user.displayName || '',
          email: user.email,
          phone: user.phoneNumber || '',
          roles: [],
          organizationIds: [],
        })
      }
      // onAuthStateChanged обработает дальнейшие действия (например, редирект)
    } catch (error) {
      if (error.code === 'auth/popup-closed-by-user') {
        console.log('Окно входа через Google было закрыто пользователем.')
      } else {
        console.error('Ошибка входа через Google:', error)
        this.errorStore.showError('Произошла ошибка при входе через Google.')
      }
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  async logout() {
    await signOut(this.auth)
  }
}

export default AuthStore
